import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Option } from '@anytag/shared-types';
import { useAllCountryOptions } from '@anytag/shared-hooks';
import { useAllExistingPackagesQuery } from '@anytag/graphql';
import { SelectorField, TextField } from '@anytag/shared-molecules';
import { Form, ModalBody, ModalTitle, ModalFooter, ModalDescription } from '@anytag/shared-atoms';
import { PackageWithAccountsFormKeys, PackageWithAccountsFormValues } from './types';

interface TemplateProps {
  count: number;
  toggleModal: () => void;
  allPackageCompanyOptions?: Option[];
}
export const Template = ({ count, toggleModal, allPackageCompanyOptions }: TemplateProps) => {
  const { t } = useTranslation();
  const { allCountryOptions } = useAllCountryOptions();
  const {
    formState: { isSubmitting },
    setValue,
    watch
  } = useFormContext<PackageWithAccountsFormValues>();
  const { data: allPackagesData } = useAllExistingPackagesQuery({ fetchPolicy: 'cache-and-network' });
  const isAddMode = watch('type') === 'add';

  const allPackageOptions =
    allPackagesData?.allExistingPackages?.map((el) => ({ label: el.name, value: `${el.id}` })) || [];

  return (
    <>
      <ModalTitle>{t(isAddMode ? 'Create a new package' : 'Select an existing package')}</ModalTitle>
      <ModalDescription>{t(isAddMode ? 'CreateNewPackage' : 'SelectPackageContent')}</ModalDescription>

      <ModalBody>
        <div>
          <div className="mt-4 flex justify-between">
            <div className="rounded-[30px] border border-border bg-light-gray px-2.5 py-1.5 text-[13px] font-semibold">
              {t('Selected account', { count })}
            </div>

            <button
              className="cursor-pointer text-[13px] font-semibold text-blue-70"
              onClick={() => setValue('type', isAddMode ? 'edit' : 'add')}
            >
              {t(isAddMode ? 'Select an existing package' : 'Create a new package')}
            </button>
          </div>

          <div className="mt-2">
            {isAddMode ? (
              <>
                <TextField<PackageWithAccountsFormKeys>
                  name="name"
                  title={t('TextForm.Package Name')}
                  placeholder={t<string>('package name')}
                  required
                />

                <Form.Fields itemsPerRow={2}>
                  <SelectorField<PackageWithAccountsFormKeys>
                    name="countryId"
                    title={t('Selector.Country')}
                    options={allCountryOptions}
                    required
                  />

                  {allPackageCompanyOptions ? (
                    <SelectorField<PackageWithAccountsFormKeys>
                      name="companies"
                      title={t('Selector.Company')}
                      options={allPackageCompanyOptions}
                      multiple
                    />
                  ) : null}
                </Form.Fields>
              </>
            ) : (
              <SelectorField<PackageWithAccountsFormKeys>
                name="id"
                title={t('Selector.Package')}
                options={allPackageOptions}
                required
              />
            )}
          </div>
        </div>
      </ModalBody>

      <ModalFooter
        cancelButton={{ onClick: toggleModal }}
        submitButton={{ type: 'submit', loading: isSubmitting, title: t(isAddMode ? 'Save&Add' : 'Add') }}
      />
    </>
  );
};
